import React from 'react';
import useGameStore from '../store/gameStore';
import { getSecteur, getPositionnement, prixConseille, priceAdjustment, demandeBaseJour } from '../utils/retail';
import { Tags } from 'lucide-react';
import { toast } from '../components/ui/sonner';

/**
 * SCD Lot 1 — Écran « Assortiment & prix ».
 * Une ligne par famille de produits : prix d'achat moyen (CUMP), prix
 * conseillé, prix de vente éditable, coefficient, marge, TVA, saisonnalité,
 * demande estimée et stock.
 */
export default function Assortiment() {
  const retail = useGameStore(s => s.retail);
  const month = useGameStore(s => s.month);
  const setPrixVente = useGameStore(s => s.setPrixVente);
  const toggleFamille = useGameStore(s => s.toggleFamille);

  if (!retail) {
    return <div className="text-slate-400 text-sm" data-testid="assortiment-empty">Aucun magasin actif.</div>;
  }
  const sec = getSecteur(retail.secteur);
  const pos = getPositionnement(retail.positionnement);

  const handlePrix = (famId, val) => {
    const prix = parseFloat(val);
    if (!Number.isFinite(prix) || prix <= 0) return;
    const res = setPrixVente(famId, Math.round(prix * 100) / 100);
    if (res?.ok && Math.abs(res.prix - prix) > 0.01) {
      toast('Prix ajusté aux bornes autorisées', { description: `Prix retenu : ${res.prix.toFixed(2)} €` });
    }
  };

  return (
    <div className="space-y-5" data-testid="assortiment-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline">{sec.nom} · {pos.nom}</p>
          <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-foreground mt-0.5 flex items-center gap-2">
            <Tags className="w-6 h-6 text-accent" /> Assortiment & prix
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Le prix influence la demande via l{"'"}élasticité-prix : baisser le prix attire plus d{"'"}acheteurs (mais réduit la marge), et inversement.
          </p>
        </div>
      </div>

      <div className="surface-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink-600 text-left">
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500">Famille</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 text-right">PA moyen</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 text-right">Conseillé</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 text-right">Prix de vente</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 text-right">Coef.</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 text-right">Marge</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 text-right">TVA</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 text-right">Saison</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 text-right">Demande/j</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 text-right">Stock</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 text-center">Actif</th>
            </tr>
          </thead>
          <tbody>
            {sec.familles.map(f => {
              const a = retail.assortiment[f.id] || {};
              const st = retail.stock[f.id] || { qty: 0, cump: f.prix_achat };
              const conseille = prixConseille(f, retail.positionnement);
              const adj = priceAdjustment(a.prixVente, conseille, f.elasticite);
              const demande = Math.round(demandeBaseJour(f, retail.secteur, retail.positionnement, retail.modele, month) * adj);
              const margePct = a.prixVente > 0 ? ((a.prixVente - st.cump) / a.prixVente) * 100 : 0;
              const saison = f.saisonnalite?.[(month - 1) % 12] ?? 1;
              const coefEffectif = st.cump > 0 ? a.prixVente / st.cump : 0;
              return (
                <tr key={f.id} className={`border-b border-ink-700/60 ${a.actif ? '' : 'opacity-40'}`} data-testid={`assort-row-${f.id}`}>
                  <td className="px-3 py-2.5">
                    <p className="font-semibold text-slate-100">{f.nom}</p>
                    <p className="text-[10px] text-slate-500 font-mono">élasticité {f.elasticite}</p>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-300">{st.cump.toFixed(2)} €</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-400">{conseille.toFixed(2)} €</td>
                  <td className="px-3 py-2.5 text-right">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      defaultValue={a.prixVente}
                      key={`${f.id}-${a.prixVente}`}
                      onBlur={e => handlePrix(f.id, e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                      className="w-24 bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-right font-mono text-slate-100 focus:outline-none focus:ring-1 focus:ring-accent"
                      data-testid={`prix-input-${f.id}`}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-300">×{coefEffectif.toFixed(2)}</td>
                  <td className={`px-3 py-2.5 text-right font-mono font-bold ${margePct >= 40 ? 'text-emerald-400' : margePct >= 15 ? 'text-slate-200' : 'text-red-400'}`}>
                    {margePct.toFixed(0)} %
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-400">{f.tva} %</td>
                  <td className={`px-3 py-2.5 text-right font-mono ${saison > 1.05 ? 'text-emerald-400' : saison < 0.95 ? 'text-amber-400' : 'text-slate-400'}`}>
                    ×{saison.toFixed(2)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-300">≈ {demande}</td>
                  <td className={`px-3 py-2.5 text-right font-mono ${st.qty === 0 ? 'text-red-400 font-bold' : 'text-slate-300'}`}>{st.qty}</td>
                  <td className="px-3 py-2.5 text-center">
                    <button
                      type="button"
                      onClick={() => toggleFamille(f.id)}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${a.actif ? 'bg-emerald-600/20 text-emerald-400' : 'bg-ink-700 text-slate-500'}`}
                      data-testid={`toggle-${f.id}`}
                    >
                      {a.actif ? 'Oui' : 'Non'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="surface-card p-4 text-xs text-slate-400 leading-relaxed">
        <p className="font-bold text-slate-300 uppercase tracking-wider text-[10px] mb-1.5">Comment lire ce tableau ?</p>
        <p>• <strong>PA moyen (CUMP)</strong> : coût unitaire moyen pondéré de votre stock — il baisse quand vous achetez avec de bonnes remises.</p>
        <p>• <strong>Coefficient</strong> : prix de vente ÷ prix d{"'"}achat. Votre positionnement <strong>{pos.nom}</strong> conseille un coefficient ×{(sec.familles[0].coefficient * pos.coef_mult).toFixed(2)} environ.</p>
        <p>• <strong>Saison</strong> : multiplicateur de demande du mois en cours. Anticipez vos achats avant les pics !</p>
      </div>
    </div>
  );
}
